import React, { Suspense, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box } from '@mui/material';
import { Breadcrumbs } from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import {
  BreadcrumbContainer,
  BreadcrumbLinkComponent,
  PageHeader,
} from 'app/components/StyledComponents';
import { useUnsoldPage } from './hooks/useUnsoldPage';
import { UnsoldMetricsWrapper } from './components/UnsoldMetricsWrapper';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import { renderComponentEnum } from 'app/pages/SalesPortalPage/LayoutV1/data/type';
import DataGridTypes from 'types/DataGridTypes';
import { metricTypeEnum } from 'app/pages/SalesPortalPage/LayoutV1/data/type';
import LoadingIndicator from 'app/components/LoadingIndicator';

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  height: "100%",
  width: "100%",
  padding: theme.spacing(3)
}));

const UnsoldPage = () => {
  const history = useHistory();

  const {
    metrics,
    loading,
    error,
    showDataGrid,
    selectedMetric,
    selectedBox,
    selectedDealerId,
    handleMetricClick,
    setShowDataGrid,
    setSelectedBox,
  } = useUnsoldPage();

  // Check if we're coming from lead details page with state
  const locationState = history.location.state as any;

  // Restore state from navigation or sessionStorage ONLY when coming from lead details page
  React.useEffect(() => {
    // Only restore if we're coming from lead details page (has locationState)
    if (locationState?.showDataGrid && metrics.length > 0) {
      // Find the metric index to restore selectedBox
      // Try multiple matching strategies
      let metricIndex = -1;
      
      // First try matching by metric name
      if (locationState.metricName) {
        metricIndex = metrics.findIndex(
          (m: any) => m.metric_name === locationState.metricName
        );
      }
      
      // If not found, try matching by type
      if (metricIndex === -1 && locationState.metricType) {
        metricIndex = metrics.findIndex(
          (m: any) => m.type === locationState.metricType || 
                     m.type === metricTypeEnum.opportunityAddedInNurturing ||
                     m.type === 'NURTURING_PROSPECTS'
        );
      }
      
      // If still not found, find any nurturing prospects metric
      if (metricIndex === -1) {
        metricIndex = metrics.findIndex(
          (m: any) => m.type === metricTypeEnum.opportunityAddedInNurturing ||
                     m.type === 'NURTURING_PROSPECTS' ||
                     m.metric_name?.toLowerCase().includes('nurturing')
        );
      }
      
      if (metricIndex !== -1) {
        const selectedMetric = metrics[metricIndex];
        // Update the hook's selectedMetric state first
        handleMetricClick(selectedMetric, metricIndex);
        
        // Also explicitly set the states
        setShowDataGrid(true);
        setSelectedBox(metricIndex);
        
        // Update breadcrumb
        setBreadcrumbsList([
          {
            label: 'Unsold',
            isActive: false,
          },
          {
            label: locationState.metricName || selectedMetric.metric_name || 'Nurturing Prospects',
            isActive: true,
          },
        ]);
      }
    }
    // Don't restore from sessionStorage on initial load - only when explicitly navigating back
  }, [locationState, metrics.length]); // Depend on metrics.length to ensure metrics are loaded

  const [breadcrumbsList, setBreadcrumbsList] = useState([
    {
      label: 'Unsold',
      isActive: true,
    },
  ]);

  // Handle metrics click for NurturingMetricsComponent
  const handleClickMetrics = (component: any, filter: any) => {
    try {
      if (component === renderComponentEnum.dataGrid) {
        // Find the metric that matches
        const metricIndex = metrics.findIndex(
          (m: any) => m.type === filter.metricType || m.metric_name === filter.metricName
        );
        
        if (metricIndex !== -1) {
          // Call the hook's handleMetricClick which updates hook state
          handleMetricClick(metrics[metricIndex], metricIndex);
          
          // Store metric name in sessionStorage for breadcrumb navigation
          sessionStorage.setItem('unsoldSelectedMetric', filter.metricName || 'Nurturing Prospects');
          
          // Update breadcrumb to include "Nurturing Prospects"
          setBreadcrumbsList([
            {
              label: 'Unsold',
              isActive: false,
            },
            {
              label: filter.metricName || 'Nurturing Prospects',
              isActive: true,
            },
          ]);
        }
      }
    } catch (error) {
      console.error('Error handling metrics click:', error);
    }
  };

  // Update breadcrumb based on showDataGrid state
  useEffect(() => {
    if (showDataGrid && selectedMetric) {
      // Find the metric name for breadcrumb
      const currentMetric = metrics.find((m: any) => 
        m.type === selectedMetric || m.type === metricTypeEnum.opportunityAddedInNurturing
      );
      if (currentMetric) {
        setBreadcrumbsList([
          {
            label: 'Unsold',
            isActive: false,
          },
          {
            label: currentMetric.metric_name || 'Nurturing Prospects',
            isActive: true,
          },
        ]);
      }
    } else {
      setBreadcrumbsList([
        {
          label: 'Unsold',
          isActive: true,
        },
      ]);
    }
  }, [showDataGrid, selectedMetric, metrics]);

  const handleClickBreadcrumbs = (breadcrumb: any, index: number) => {
    try {
      if (breadcrumb.isActive) {
        return;
      }
      // If clicking on "Unsold" breadcrumb, close data grid and reset
      if (breadcrumb.label === 'Unsold' && showDataGrid) {
        setShowDataGrid(false);
        setSelectedBox(-1);
        setBreadcrumbsList([
          {
            label: 'Unsold',
            isActive: true,
          },
        ]);
      }
    } catch (error) {
      console.error('Error handling breadcrumb click:', error);
    }
  };

  // Helper function to get time period title based on value
  const getTimePeriodTitle = (timePeriod: string): string => {
    const titleMapping: { [key: string]: string } = {
      'LAST_MONTH': 'Last 30 Days',
      'LAST_3_MONTH': 'Last 3 Months',
      'LAST_3_MONTHS': 'Last 3 Months',
      'SIX_MONTHS': 'Last 6 Months',
      'TODAY': 'Today',
      'YESTERDAY': 'Yesterday',
      'THIS_WEEK': 'This Week',
      'LAST_WEEK': 'Last Week',
      'WEEK_TO_DATE': 'Week to Date',
      'THIS_MONTH': 'This Month',
      'MONTH_TO_DATE': 'Month to Date',
      'THIS_QUARTER': 'This Quarter',
      'LAST_QUARTER': 'Last Quarter',
      'QUARTER_TO_DATE': 'Quarter to Date',
      'THIS_YEAR': 'This Year',
      'LAST_YEAR': 'Last Year',
      'YEAR_TO_DATE': 'Year to Date',
      'ALL_TIME': 'All Time',
      '30_DAYS': 'Last 30 Days',
      '7_DAYS': 'Last 7 Days',
      '90_DAYS': 'Last 90 Days',
      '180_DAYS': 'Last 180 Days',
      '365_DAYS': 'Last 365 Days',
    };
    
    return titleMapping[timePeriod] || 'Last 30 Days';
  };

  // Default time period from environment variable
  const timeperiodSelect = process.env.REACT_APP_UNSOLD_LEADS_TIMEPERIOD_DEFAULT || 'LAST_MONTH';
  const mysqlFlag = true; // Default to true as per API requirement
  
  // Get title based on time period
  const pageTitle = `Unsold Prospects - ${getTimePeriodTitle(timeperiodSelect)}`;

  // Handle cell click in DataGrid
  const handleCellClick = (cell: any, clickedElement?: string) => {
    try {
      // Check if the clicked cell is the name column
      // The cell structure from DataGridMAIQ: cell.field, cell.row, cell.value, etc.
      if (cell?.field === 'name' && cell?.row) {
        // Extract lead ID from the row data
        // The row data structure may vary, so we'll try common fields
        const rowData = cell.row;
        const leadId = 
          rowData.id || 
          rowData._id || 
          rowData.lead_id || 
          rowData.leadId ||
          rowData.leadscore_id ||
          rowData.leadscoreId;
        
        if (leadId) {
          // Navigate to lead details page
          history.push(`/ai-avatar-layout/unsold/lead/${leadId}`);
        } else {
          console.warn('Lead ID not found in row data. Available fields:', Object.keys(rowData));
          // Fallback: use row index or first available ID field
          const firstIdField = Object.keys(rowData).find(key => 
            key.toLowerCase().includes('id') || key.toLowerCase().includes('_id')
          );
          if (firstIdField && rowData[firstIdField]) {
            history.push(`/ai-avatar-layout/unsold/lead/${rowData[firstIdField]}`);
          }
        }
      }
    } catch (error) {
      console.error('Error handling cell click:', error);
    }
  };

  if (!selectedDealerId) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 66px)',
          padding: '0 48px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ typography: 'h4', color: 'text.secondary' }}>
          Please select a dealer
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={`${pageTitle} - Myautoiq`} />
      </Helmet>
      <Box
        sx={{
          height: 'calc(100vh - 66px)',
          padding: '0 48px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ marginTop: '48px', marginBottom: '48px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <BreadcrumbContainer>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<NavigateNextIcon fontSize="small" />}
              >
                {breadcrumbsList?.map((breadcrumb, index) => (
                  <BreadcrumbLinkComponent
                    key={`${breadcrumb.label}-${breadcrumb.isActive}-${index}`}
                    underline={breadcrumb.isActive ? 'none' : 'always'}
                    color={breadcrumb.isActive ? 'text.primary' : 'inherit'}
                    onClick={() => handleClickBreadcrumbs(breadcrumb, index)}
                    sx={{ 
                      cursor: breadcrumb.isActive ? 'default' : 'pointer',
                      color: breadcrumb.isActive ? 'text.primary' : 'inherit',
                    }}
                  >
                    {breadcrumb.label}
                  </BreadcrumbLinkComponent>
                ))}
              </Breadcrumbs>
            </BreadcrumbContainer>
            <PageHeader sx={{ marginBottom: '0 !important' }}>
              {pageTitle}
            </PageHeader>
          </Box>
        </Box>

        <MainContainer>
          {/* Metrics Section */}
          <Box sx={{ width: '100%' }}>
            <Suspense fallback={<LoadingIndicator />}>
              <UnsoldMetricsWrapper
                metrics={metrics}
                onClickMetrics={handleClickMetrics}
                selectedMetric={selectedMetric}
                selectedBox={selectedBox || -1}
                setSelectedBox={setSelectedBox}
                isLoading={loading}
              />
            </Suspense>
          </Box>

          {/* Error Message */}
          {error && (
            <Box sx={{ p: 2, backgroundColor: 'error.light', borderRadius: 1 }}>
              {error}
            </Box>
          )}

          {/* DataGrid Section - Only show when Nurturing Prospects is clicked */}
          {showDataGrid && selectedMetric && (
            <Box>
              <Suspense fallback={<LoadingIndicator />}>
                <DataGridMAIQ
                  key={`${selectedDealerId}-${selectedMetric}-${timeperiodSelect}`}
                  type={DataGridTypes.UNSOLD(
                    selectedDealerId,
                    timeperiodSelect,
                    mysqlFlag,
                  )}
                  onCellClick={handleCellClick}
                />
              </Suspense>
            </Box>
          )}
        </MainContainer>
      </Box>
    </>
  );
};

export default UnsoldPage;

